import { Plus, Save, X } from "lucide-react";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { Card, PageHeader } from "../../components/ui/patrones";
import { Aviso, Boton } from "../../components/ui/primitivos";
import {
  crearPresupuesto,
  fetchCatalogo,
  fetchClientes,
  fetchProductos,
} from "../../lib/consola-api";
import { pesos } from "../../lib/formato";
import { useDatos } from "../../lib/useDatos";
import { type ItemEnCarga, itemVacio, RenglonConsulta } from "./RenglonConsulta";
import type { PrecioResuelto } from "./renglones";
import { cuerpoDeRenglon, primerRenglonIncompleto, totalPresupuesto } from "./renglones";

/**
 * Alta de una consulta — porteada de `CreateBudget` + `BudgetForm` de
 * `imprenta`, pantalla completa y no modal.
 *
 * **«Consulta» es como le dice el cliente**, y el nombre se mantiene: es el
 * término con el que la imprenta habla con quien pide el trabajo. En la base y
 * en la API sigue siendo `presupuesto`.
 *
 * Lo que define la experiencia y por eso se replica igual: los ítems se editan
 * **en su lugar** —se agrega uno vacío y se completa ahí— con el precio
 * resolviéndose solo, el subtotal de cada uno a la derecha y el Total General al
 * pie. Un ítem **sin tarifa no bloquea**: se guarda «Pendiente cotizar», porque
 * la consulta suele armarse antes de que el precio exista.
 */
export function NuevaConsultaPagina() {
  const navegar = useNavigate();
  const clientes = useDatos(() => fetchClientes());
  const productos = useDatos(() => fetchProductos());
  const formasPago = useDatos(() => fetchCatalogo("formas-pago"));

  const [clienteId, setClienteId] = useState("");
  // Arranca en HOY, como `imprenta` (`BudgetForm`): la consulta se cotiza con
  // los precios de hoy, así que esa es la fecha que la explica.
  const [validoHasta, setValidoHasta] = useState(() => new Date().toISOString().slice(0, 10));
  const [formaPagoId, setFormaPagoId] = useState("");
  const [items, setItems] = useState<ItemEnCarga[]>([itemVacio()]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** El ítem que frenó el guardado: se marca y se le lleva el foco. */
  const [claveConError, setClaveConError] = useState<string | null>(null);

  /**
   * Anota el precio que resolvió un ítem.
   *
   * **Estable a propósito** (`useCallback` sin dependencias): el ítem la usa
   * dentro del efecto que dispara la consulta. Una función nueva en cada render
   * volvería a lanzar la consulta al recibir su propia respuesta, en bucle.
   */
  const anotarPrecio = useCallback((clave: string, precio: PrecioResuelto | null) => {
    setItems((prev) => prev.map((i) => (i.clave === clave ? { ...i, precioResuelto: precio } : i)));
  }, []);

  const cliente = (clientes.datos ?? []).find((c) => c.id === clienteId);
  const total = totalPresupuesto(items);

  /**
   * Guardar NO se deshabilita por lo que falte cargar, solo mientras guarda.
   *
   * Es la forma de `imprenta`: el botón está siempre, y lo que falta se dice al
   * intentar. Un botón gris no explica qué le falta, y con dos cards y varios
   * ítems ese «qué» no es evidente.
   *
   * Un ítem a medio cargar **frena el guardado**; no se descarta. Descartarlo
   * dejaría una consulta con menos renglones de los que se ven en pantalla, sin
   * avisar: el que carga cree que guardó dos y guardó uno.
   */
  async function guardar() {
    if (!clienteId) {
      setError("Elegí un cliente para la consulta.");
      return;
    }
    if (items.length === 0) {
      setError("La consulta necesita al menos un item.");
      return;
    }
    const incompleto = primerRenglonIncompleto(items);
    if (incompleto) {
      setClaveConError(incompleto.clave);
      setError("Hay un item sin completar: elegí el producto y una cantidad mayor a cero.");
      return;
    }
    setClaveConError(null);
    setGuardando(true);
    setError(null);
    try {
      const { presupuesto } = await crearPresupuesto({
        clienteId,
        ...(formaPagoId ? { formaPagoId } : {}),
        ...(validoHasta ? { validoHasta } : {}),
        items: items.map((i) =>
          cuerpoDeRenglon({
            clave: i.clave,
            productoId: i.productoId,
            productoNombre: i.productoNombre,
            tamanoId: i.tamanoId,
            baseId: i.baseId,
            cantidad: i.cantidad,
          }),
        ),
      });
      navegar(`/presupuestos?nueva=${presupuesto.numero ?? ""}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo crear la consulta");
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        titulo="Nueva Consulta"
        subtitulo="Crea una nueva consulta para un cliente."
        alVolver={() => navegar("/presupuestos")}
        acciones={
          <>
            <Boton onClick={guardar} disabled={guardando}>
              <Save className="mr-2 inline h-4 w-4" />
              {guardando ? "Guardando..." : "Guardar"}
            </Boton>
            <Boton variante="secundario" onClick={() => navegar("/presupuestos")}>
              <X className="mr-2 inline h-4 w-4" />
              Cancelar
            </Boton>
          </>
        }
      />

      {error ? <Aviso>{error}</Aviso> : null}

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="p-6">
          <h3 className="mb-4 font-semibold text-lg text-neutral-900">Cliente</h3>
          <label htmlFor="cliente" className="mb-1 block font-medium text-neutral-700 text-sm">
            Cliente *
          </label>
          <select
            id="cliente"
            value={clienteId}
            onChange={(e) => setClienteId(e.target.value)}
            className="w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-neutral-900 text-sm"
          >
            <option value="">Selecciona un cliente...</option>
            {(clientes.datos ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombreFantasia || c.razonSocial}
              </option>
            ))}
          </select>
        </Card>

        <Card className="p-6">
          <h3 className="mb-4 font-semibold text-lg text-neutral-900">Fechas</h3>
          <div className="space-y-4">
            <div>
              <label
                htmlFor="valido-hasta"
                className="mb-1 block font-medium text-neutral-700 text-sm"
              >
                Válido hasta
              </label>
              <input
                id="valido-hasta"
                type="date"
                value={validoHasta}
                onChange={(e) => setValidoHasta(e.target.value)}
                className="w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-neutral-900 text-sm"
              />
              <p className="mt-1 text-neutral-500 text-xs">
                Opcional. Fecha límite de validez de la consulta.
              </p>
            </div>
            <div>
              <label
                htmlFor="forma-pago"
                className="mb-1 block font-medium text-neutral-700 text-sm"
              >
                Forma de Pago
              </label>
              <select
                id="forma-pago"
                value={formaPagoId}
                onChange={(e) => setFormaPagoId(e.target.value)}
                className="w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-neutral-900 text-sm"
              >
                <option value="">Sin especificar</option>
                {(formasPago.datos ?? []).map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nombre}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-neutral-500 text-xs">
                Opcional. Condiciones de pago para esta consulta.
              </p>
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <div className="mb-4 flex flex-row items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg text-neutral-900">Items</h3>
            <p className="text-neutral-500 text-sm">
              Seleccione productos del catalogo para cada item.
            </p>
          </div>
          <Boton variante="secundario" onClick={() => setItems((prev) => [...prev, itemVacio()])}>
            <Plus className="mr-2 inline h-4 w-4" />
            Agregar Item
          </Boton>
        </div>

        <div className="space-y-4">
          {items.map((item) => (
            <RenglonConsulta
              key={item.clave}
              item={item}
              productos={productos.datos ?? []}
              clienteId={clienteId}
              clienteEsVip={cliente?.esVip ?? false}
              deshabilitado={guardando}
              conError={claveConError === item.clave}
              puedeQuitar={items.length > 1}
              alCambiar={(nuevo) => {
                if (claveConError === nuevo.clave) setClaveConError(null);
                setItems((prev) => prev.map((i) => (i.clave === nuevo.clave ? nuevo : i)));
              }}
              alPrecio={anotarPrecio}
              alQuitar={() => setItems((prev) => prev.filter((i) => i.clave !== item.clave))}
            />
          ))}

          {items.length === 0 ? (
            <div className="py-8 text-center text-neutral-500">
              No hay items en la consulta. Agrega uno para comenzar.
            </div>
          ) : null}

          {items.length > 0 ? (
            <div className="flex items-center justify-end border-neutral-200 border-t pt-4">
              <div className="text-right">
                <span className="mr-4 text-neutral-500 text-sm">Total General</span>
                {total === null ? (
                  <span className="font-medium text-amber-600 text-lg">
                    Pendiente de cotización
                  </span>
                ) : (
                  <span className="font-bold text-2xl text-brand-600">{pesos(total)}</span>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </Card>
    </div>
  );
}
